import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider, useQuery, useQueryClient, } from "@tanstack/react-query";
import { AnimatePresence, motion } from "motion/react";
import { memberQueryKey, memberQueryOptions } from "./queries";
function readMemberSlugFromHash() {
    const raw = window.location.hash || "";
    if (!raw.toLowerCase().startsWith("#modal"))
        return null;
    const qsIndex = raw.indexOf("?");
    if (qsIndex === -1)
        return null;
    const params = new URLSearchParams(raw.slice(qsIndex + 1));
    const uid = params.get("uid");
    return uid || null;
}
const useSelectedMemberSlug = () => {
    const [selectedSlug, setSelectedSlug] = React.useState(() => readMemberSlugFromHash());
    const selectedSlugRef = React.useRef(selectedSlug);
    useEffect(() => {
        selectedSlugRef.current = selectedSlug;
    }, [selectedSlug]);
    const queryClient = useQueryClient();
    const onClick = (e) => {
        var _a;
        const target = e.target;
        const card = (_a = target === null || target === void 0 ? void 0 : target.closest) === null || _a === void 0 ? void 0 : _a.call(target, "a.card");
        if (!card)
            return;
        const slugAttr = card.getAttribute("data-slug");
        if (!slugAttr)
            return;
        e.preventDefault();
        setSelectedSlug(slugAttr);
    };
    const onKeyDown = (e) => {
        if (e.key === "Escape")
            setSelectedSlug(null);
    };
    const onMouseOver = (e) => {
        var _a;
        const target = e.target;
        const card = (_a = target === null || target === void 0 ? void 0 : target.closest) === null || _a === void 0 ? void 0 : _a.call(target, "a.card");
        if (!card)
            return;
        const slugAttr = card.getAttribute("data-slug");
        if (!slugAttr)
            return;
        if (queryClient.getQueryState(memberQueryKey(slugAttr)))
            return;
        queryClient.prefetchQuery(memberQueryOptions(slugAttr));
    };
    useEffect(() => {
        if (selectedSlug) {
            document.body.classList.add("modal-open");
        }
        else {
            document.body.classList.remove("modal-open");
        }
    }, [selectedSlug]);
    useEffect(() => {
        if (selectedSlug) {
            window.location.hash = `modal?uid=${encodeURIComponent(selectedSlug)}`;
            return;
        }
        if (window.location.hash && readMemberSlugFromHash() !== null) {
            history.pushState("", document.title, window.location.pathname + window.location.search);
        }
    }, [selectedSlug]);
    useEffect(() => {
        const onHashChange = () => {
            const slugFromHash = readMemberSlugFromHash();
            if (slugFromHash) {
                setSelectedSlug(slugFromHash);
                return;
            }
            if (selectedSlugRef.current === null) {
                setSelectedSlug(null);
            }
        };
        window.addEventListener("hashchange", onHashChange);
        return () => window.removeEventListener("hashchange", onHashChange);
    }, []);
    useEffect(() => {
        document.addEventListener("click", onClick);
        return () => document.removeEventListener("click", onClick);
    }, []);
    useEffect(() => {
        document.addEventListener("mouseover", onMouseOver);
        return () => document.removeEventListener("mouseover", onMouseOver);
    }, [queryClient]);
    useEffect(() => {
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, []);
    return { selectedSlug, setSelectedSlug };
};
const MemberModal = ({ slug, onClose, }) => {
    var _a, _b, _c;
    const { data, isLoading, error } = useQuery(memberQueryOptions(slug));
    return (_jsxs(_Fragment, { children: [_jsx(motion.div, { role: "dialog", "aria-modal": "true", onMouseDown: (e) => {
                    if (e.target === e.currentTarget)
                        onClose();
                }, onClickCapture: (e) => {
                    var _a;
                    const target = e.target;
                    const anchor = (_a = target === null || target === void 0 ? void 0 : target.closest) === null || _a === void 0 ? void 0 : _a.call(target, "a");
                    if (!anchor)
                        return;
                    const href = anchor.getAttribute("href") || "";
                    if (!href.startsWith("#"))
                        return;
                    if (href.toLowerCase().startsWith("#modal"))
                        return;
                    e.preventDefault();
                }, id: "modal", className: "modal fade in", style: { display: "block" }, initial: { opacity: 0, y: "-25%" }, animate: { opacity: 1, y: "0%" }, exit: { opacity: 0, y: "-25%" }, transition: {
                    duration: 0.3,
                    ease: "easeOut",
                }, children: _jsx("div", { className: "modal-dialog", children: _jsxs("div", { className: "modal-content", children: [_jsx("div", { className: "modal-header", children: _jsx("button", { type: "button", className: "close", "data-dismiss": "modal", "aria-label": "Close", onClick: onClose, children: _jsx("span", { "aria-hidden": "true", children: "\u00D7" }) }) }), _jsxs("div", { className: "modal-body", children: [isLoading && (_jsx("div", { className: "modal-loading", children: _jsx("i", { className: "fa fa-spinner fa-spin" }) })), error && (_jsx("div", { className: "modal-error", children: _jsx("div", { children: "Failed to load." }) })), (data === null || data === void 0 ? void 0 : data.data) && (_jsxs("div", { className: "media", children: [_jsx("div", { className: "media-left", children: _jsxs("picture", { className: "modal-img", children: [_jsx("source", { srcSet: data.data.featured_img || "" }), _jsx("img", { src: "data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==", alt: data.data.title, title: data.data.title })] }) }), _jsxs("div", { className: "media-body", children: [_jsxs("div", { className: "media-headings", children: [_jsxs("h4", { className: "modal-title", children: [_jsx("span", { className: "title-en", children: data.data.title }), data.data.title_zh ? _jsx("span", { className: "title-zh", children: data.data.title_zh }) : null] }), _jsxs("div", { className: "modal-role", children: [_jsx("span", { className: "role-en", children: (_a = data.data.role) === null || _a === void 0 ? void 0 : _a.title }), ((_b = data.data.role) === null || _b === void 0 ? void 0 : _b.title_zh) && _jsxs("span", { className: "role-zh", children: [" ", (_c = data.data.role) === null || _c === void 0 ? void 0 : _c.title_zh] })] })] }), data.data.content && (_jsx("div", { className: "modal-desc", dangerouslySetInnerHTML: { __html: data.data.content } })), data.data.email && (_jsxs("div", { className: "modal-email", children: [_jsx("i", { className: "fa-regular fa-envelope" }), _jsx("a", { target: "_blank", rel: "noopener noreferrer", href: `mailto:${data.data.email}`, children: data.data.email })] })), data.data.reported_cases && (_jsxs("div", { className: "modal-cases", children: [_jsx("p", { children: _jsx("strong", { children: "Reported cases:" }) }), _jsx("div", { dangerouslySetInnerHTML: { __html: data.data.reported_cases } })] }))] })] }))] })] }) }) }), _jsx(motion.div, { className: "modal-backdrop", initial: { opacity: 0 }, animate: { opacity: .5 }, exit: { opacity: 0 }, transition: {
                    duration: 0.15,
                    ease: "linear",
                } })] }));
};
const App = () => {
    const { selectedSlug, setSelectedSlug } = useSelectedMemberSlug();
    return (_jsx(AnimatePresence, { children: selectedSlug !== null ? (_jsx(MemberModal, { slug: selectedSlug, onClose: () => setSelectedSlug(null) })) : null }));
};
const queryClient = new QueryClient();
const el = document.getElementById("root");
if (el) {
    createRoot(el).render(_jsx(QueryClientProvider, { client: queryClient, children: _jsx(App, {}) }));
}
